import React, { memo, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";
import { useMeasurementResultDoc } from "../hooks/results/useMeasurementResultDoc";
import { parseTimestampFromRecordingId } from "../utils/results/recordingIdTime";

// Variant views
import PulseScanResultView from "../components/results/views/PulseScanResultView";
import SpirometryResultView from "../components/results/views/SpirometryResultView";
import TidalBreathingResultView from "../components/results/views/TidalBreathingResultView";
import {
  getUploadFailedMessage,
  isUploadFailedDoc,
} from "../utils/results/isUploadFailedDoc";

// Choose by context first; fallback to variant
function resolveResultView({ variant, context, docData }) {
  const docContext =
    (typeof docData?.appMode === "string" && docData.appMode.toLowerCase()) ||
    null;

  const ctx = (context || docContext || "").toLowerCase();

  if (variant === "RecordingSpirometry") return SpirometryResultView;

  // Context-based routing (new world)
  if (ctx === "pulse") return PulseScanResultView;
  if (ctx === "respiratory") return TidalBreathingResultView;

  // Legacy fallback by variant (old world)
  if (variant === "RecordingPulseScan") return PulseScanResultView;
  if (variant === "RecordingTidalBreathing") return TidalBreathingResultView;

  return null;
}

function computeAgeMs(docId) {
  const ts = parseTimestampFromRecordingId(docId);
  if (!ts) return null;
  return Date.now() - ts.getTime();
}

function ResultPage({ db, user }) {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const variant = params.get("variant") || "";
  const docId = params.get("docid") || params.get("docId") || "";
  const contextParam = params.get("context") || params.get("appMode") || ""; // NEW
  const uid = user?.uid || null;

  const { data, exists, loading, error } = useMeasurementResultDoc(
    db,
    uid,
    variant,
    docId,
  );

  useEffect(() => {
    trackEvent("result_view", {
      variant: variant || null,
      docId: docId || null,
      lang: i18n.language || null,
    });
  }, [variant, docId, i18n.language]);

  const ageMs = useMemo(() => computeAgeMs(docId), [docId]);
  const isWithin5Min = typeof ageMs === "number" ? ageMs < 5 * 60 * 1000 : true;

  const VariantView = useMemo(() => {
    return resolveResultView({
      variant,
      context: contextParam,
      docData: data, // may be null while loading
    });
  }, [variant, contextParam, data]);

  const uploadFailed = useMemo(
    () => !!exists && isUploadFailedDoc(data),
    [exists, data],
  );

  const uploadFailInfo = useMemo(() => getUploadFailedMessage(data), [data]);

  // Extract a generic “isReady” signal from the variant view.
  // Each view defines how to know whether it’s ready (HR exists, FEV1 exists, etc).
  const isReady = useMemo(() => {
    if (!VariantView) return false;
    // convention: view has a static method
    return !!exists && VariantView.isReady?.(data, { docId, variant }) === true;
  }, [VariantView, exists, data, docId, variant]);

  const isTimeout = useMemo(() => {
    if (!VariantView) return false;
    const ready = !!exists && VariantView.isReady?.(data, { docId, variant });
    return !!exists && !ready && !isWithin5Min;
  }, [VariantView, exists, data, docId, variant, isWithin5Min]);

  if (error || !variant || !docId || !uid) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t("result.couldntLoadRes", "Couldn't load result.")}
          </h2>
          <p className="muted-text">
            {t("result.timeoutBody", "Please try again.")}
          </p>
          <p className="muted-text">{error?.message || "Unknown error"}</p>

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate("/results")}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!VariantView) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t(
              "result.unsupported",
              "Result view not implemented for this mode",
            )}
          </h2>
          <p className="muted-text">{variant}</p>

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate("/results")}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // UPLOAD FAILED (terminal)
  if (!loading && uploadFailed) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t("result.uploadFailedTitle", "Upload failed")}
          </h2>

          <p className="muted-text">
            {t(
              "result.uploadFailedBody",
              "We couldn't upload your recording. Please check your connection and try again.",
            )}
          </p>

          {uploadFailInfo?.msg ? (
            <p className="muted-text">{uploadFailInfo.msg}</p>
          ) : null}

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate("/results")}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // TIMEOUT (doc exists, but not “ready” after 5 mins)
  if (!loading && isTimeout) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <p className="subtitle-text">
            {t("result.timeoutTitle", "Results not available")}
          </p>
          <p className="muted-text">
            {t("result.timeoutBody", "Please try again.")}
          </p>
          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate("/results")}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // WAIT (still computing within window)
  if (loading || !isReady) {
    return (
      <div className="w-full max-w-lg h-full p-4 flex flex-col items-center justify-center text-center">
        <p className="subtitle-text">
          {t("result.wait", "Please wait for results")}
        </p>
      </div>
    );
  }

  // READY → delegate render to variant
  return (
    <VariantView
      data={data}
      docId={docId}
      variant={variant}
      onFinish={() => navigate("/results")}
    />
  );
}

export default memo(ResultPage);
